/** @jsxImportSource @emotion/react */
import * as diacritics from "diacritics";
import * as authSelectors from "./authSelectors";
import * as authActions from "./authActions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "../../appRoutingHelpers";
import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { breakpoints } from "../../components/breakpoints";
import { effects } from "../../components/effects";
import { Avatar } from "../../components/Avatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SearchTextFieldWithKeyboardInteraction } from "../../components/forms/fields/SearchTextFieldWithKeyboardInteraction";
import { Organization } from "../../model/Organization";
import { CardTableEmptyPlaceholder } from "../../components/table-components/CardTableEmptyPlaceholder";

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  padding: 40px;
  height: 100vh;
  width: 100vw;
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  justify-content: start;
  padding-bottom: 24px;
`;

const orgsGridStyle = css`
  display: grid;
  column-gap: 16px;
  row-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr;
  ${breakpoints.mobile} {
    grid-template-columns: 1fr;
  }
  ${breakpoints.tablet} {
    grid-template-columns: 1fr;
  }
  ${breakpoints.desktop} {
    grid-template-columns: 1fr 1fr;
  }
  ${breakpoints.hd} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const organizationButtonStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;
  padding: 16px;
  border: 0px;
  align-items: center;
  border-radius: 8px;
  background-color: ${colors.white};
  cursor: pointer;
  box-shadow: ${effects.buttonPrimary};
  &:hover {
    background-color: ${colors.whiteHover};
  }
`;

const avatarAndInfoWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const organizationInfoWrapperStyle = css`
  display: flex;
  justify-content: start;
  align-items: start;
  flex-direction: column;
  gap: 8px;
`;

const headerWrapperStyle = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  justify-content: space-between;
  align-items: center;
  ${breakpoints.desktop} {
    flex-direction: row;
    margin-bottom: 0px;
  }
`;

const searchFieldWrapperStyle = css`
  width: 100%;
  ${breakpoints.desktop} {
    width: 400px;
  }
`;

export const SelectOrganizationList = () => {
  const [searchValue, setSearchValue] = useState<string>("");
  const dispatch = useDispatch();
  const allUserOrganizations = useSelector(authSelectors.organizations);
  const user = useSelector(authSelectors.name);
  const navigate = useNavigate();
  const selectOrganization = (organization: string) => {
    dispatch(authActions.selectOrganizationAction(organization));
    navigate(routes.home(organization));
  };

  const returnFilteredOrganizationList = (
    organizations: Organization[],
    searchValue: string
  ) => {
    const sanitizedSearchValue = diacritics.remove(
      searchValue.toLowerCase().replace(/\W+/g, "")
    );
    return organizations.filter((p) =>
      diacritics
        .remove(p.name.toLowerCase().replace(/\W+/g, ""))
        .includes(sanitizedSearchValue)
    );
  };

  useEffect(() => {
    if (!allUserOrganizations) {
      return;
    }
    if (allUserOrganizations.length !== 1) {
      return;
    }
    const keyName = allUserOrganizations[0].keyName;
    dispatch(authActions.selectOrganizationAction(keyName));
    navigate(routes.home(keyName));
  }, [allUserOrganizations, navigate, dispatch]);

  return (
    <div css={wrapperStyle}>
      <div css={headerWrapperStyle}>
        <div css={headerStyle}>
          <StyledText
            className="bodyHeaderOneSemi mb-0"
            color={colors.textPrimary}
          >
            Select organization
          </StyledText>
          <StyledText
            className="bodyTextRegular mb-0"
            color={colors.textPrimary}
          >
            Hey, {user} 👋 Select an organization to continue.
          </StyledText>
        </div>
        <div css={searchFieldWrapperStyle}>
          <SearchTextFieldWithKeyboardInteraction
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            placeholder="Search for organization..."
          />
        </div>
      </div>
      {returnFilteredOrganizationList(allUserOrganizations, searchValue)
        .length === 0 ? (
        <CardTableEmptyPlaceholder>
          No organization match your search
        </CardTableEmptyPlaceholder>
      ) : (
        <div css={orgsGridStyle}>
          {returnFilteredOrganizationList(allUserOrganizations, searchValue)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((o) => (
              <button
                key={o.keyName}
                css={organizationButtonStyle}
                onClick={(e) => {
                  e.preventDefault();
                  selectOrganization(o.keyName);
                }}
              >
                <div css={avatarAndInfoWrapperStyle}>
                  <Avatar
                    size="large"
                    rounded={false}
                    backgroundColor={colors.ctaLight}
                  >
                    {o.name.charAt(0)}
                  </Avatar>
                  <div css={organizationInfoWrapperStyle}>
                    <StyledText className="bodyTextSemibold mb-0">
                      {o.name}
                    </StyledText>
                    <StyledText className="bodySubtextRegular mb-0">
                      Click to choose organization
                    </StyledText>
                  </div>
                </div>
                <FontAwesomeIcon icon={solid("arrow-right")} />
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
